import { corsFetchPath } from "../cors-address.ts";

/**
 * The piece sheets as GPU textures (ADR-094 decision 16): each sheet a look needs is fetched once, a few at a time,
 * becomes a texture once and stays, so composing a look is a handful of draws with no upload.
 *
 * The cache is counted in sheets and holds at most the sheets the page's pointer names (64 for the first table, 0.64
 * MiB each as textures); it lets none go.
 *
 * A sheet that fails to load is asked for again in rounds: every failed sheet a look still needs, together, after a
 * wait that starts at `retryMs` and doubles with each round up to `retryMaxMs`, and starts over once nothing is
 * failing, fetching or waiting. A failed sheet no look needs any more is forgotten at the round, and fetched afresh
 * if a look asks for it later. Until a sheet arrives the looks that need it wear the fallback. The wait is the
 * store's, not a sheet's: one sheet that never loads keeps every later failure at the longer wait.
 *
 * A texture already under a sheet's key is used as it is and never removed: a page has one look cache
 * (`look-cache.ts`), so such a texture is another's.
 */

/**
 * The texture manager as the sheets use it: Phaser's, or a test's.
 */
export interface SheetTextures {
  /**
   * Makes a texture of a loaded image under a key.
   *
   * @param key The texture's key.
   * @param source The image.
   * @returns The texture, or null when the key is taken.
   */
  addImage(key: string, source: HTMLImageElement): unknown;
  /**
   * Whether a texture has the key.
   *
   * @param key The texture's key.
   * @returns True when it exists.
   */
  exists(key: string): boolean;
  /**
   * Removes a texture.
   *
   * @param key The texture's key.
   */
  remove(key: string): unknown;
}

/**
 * Loads one image and says how it ended, exactly once.
 */
export type SheetLoader = (url: string, loaded: (image: HTMLImageElement) => void, failed: () => void) => void;

/**
 * The key a sheet's texture has.
 *
 * @param id The sheet's id.
 * @returns The key.
 */
export function sheetTextureKey(id: string): string {
  return `look-sheet:${id}`;
}

/**
 * Loads an image and decodes it before it is handed on, so its upload does not decode it in a frame.
 *
 * @param url The image's address.
 * @param loaded Told the decoded image.
 * @param failed Told when it did not load or decode.
 */
function loadImage(url: string, loaded: (image: HTMLImageElement) => void, failed: () => void): void {
  const image = new Image();
  image.crossOrigin = "anonymous";
  image.src = url;
  image.decode().then(
    () => loaded(image),
    () => failed(),
  );
}

/**
 * Where a sheet stands: waiting its turn, being fetched, a texture the sheets made, failed and waiting to be asked
 * for again, or a texture another made under its key.
 */
const SheetState = { Queued: 0, Fetching: 1, Loaded: 2, Failed: 3, Borrowed: 4 } as const;
type SheetState = (typeof SheetState)[keyof typeof SheetState];

/**
 * The piece sheets of one page.
 */
export class LookSheets {
  private readonly states = new Map<string, SheetState>();
  private readonly queue: string[] = [];
  private fetching = 0;
  private held = 0;
  private failed = 0;
  private retryAt = Number.POSITIVE_INFINITY;
  private wait: number;
  private stopped = false;
  private readonly warned = new Set<string>();

  /**
   * Told each sheet's id as its fetch fails, before it waits to be asked for again; nobody when null.
   */
  public onFailed: ((id: string) => void) | null = null;

  /**
   * @param textures Where the textures are made.
   * @param base The asset base the pointer's paths are relative to.
   * @param paths The pointer's sheets: each sheet id's path.
   * @param limits How many sheets are fetched at once, how long a failed one waits before it is asked for again, and
   *   the most that wait grows to.
   * @param onLoaded Told each sheet's id as its texture is made.
   * @param needed Whether a look still needs a sheet, asked of each failed sheet as its round comes.
   * @param load What loads an image; the page's unless a test hands in another.
   */
  public constructor(
    private readonly textures: SheetTextures,
    private readonly base: string,
    private readonly paths: Readonly<Record<string, string>>,
    private readonly limits: { readonly fetches: number; readonly retryMs: number; readonly retryMaxMs: number },
    private readonly onLoaded: (id: string) => void,
    private readonly needed: (id: string) => boolean,
    private readonly load: SheetLoader = loadImage,
  ) {
    this.wait = limits.retryMs;
  }

  /**
   * How many sheets are textures now.
   *
   * @returns The count, at most the sheets the pointer names.
   */
  public get loaded(): number {
    return this.held;
  }

  /**
   * How many sheets are being fetched now.
   *
   * @returns The count, at most the limit.
   */
  public get inFlight(): number {
    return this.fetching;
  }

  /**
   * How many sheets wait their turn to be fetched.
   *
   * @returns The count.
   */
  public get waiting(): number {
    return this.queue.length;
  }

  /**
   * Whether a sheet is a texture.
   *
   * @param id The sheet's id.
   * @returns True once it has loaded.
   */
  public has(id: string): boolean {
    const state = this.states.get(id);
    return state === SheetState.Loaded || state === SheetState.Borrowed;
  }

  /**
   * Asks for a sheet: fetched once, in its turn. A sheet already asked for, loaded or waiting out a failure is left as
   * it is, and one the pointer does not name is ignored.
   *
   * @param id The sheet's id.
   */
  public request(id: string): void {
    if (this.stopped || this.states.has(id) || !Object.hasOwn(this.paths, id)) {
      return;
    }
    this.states.set(id, SheetState.Queued);
    this.queue.push(id);
    this.pump();
  }

  /**
   * Runs a round for the sheets that failed, once the wait since the first of them was noticed has passed: each one a
   * look still needs is asked for again, the rest are forgotten, and the next round's wait is twice this one's, up to
   * the cap. Called each frame; it allocates nothing, and does nothing at all while nothing has failed.
   *
   * @param nowMs The time in ms.
   */
  public retry(nowMs: number): void {
    if (this.failed === 0) {
      return;
    }
    if (this.retryAt === Number.POSITIVE_INFINITY) {
      this.retryAt = nowMs + this.wait;
      return;
    }
    if (nowMs < this.retryAt) {
      return;
    }
    this.retryAt = Number.POSITIVE_INFINITY;
    this.failed = 0;
    this.wait = Math.min(this.wait * 2, this.limits.retryMaxMs);
    this.requeue();
  }

  /**
   * Puts every failed sheet a look still needs back in the queue, forgets the others, and starts what the limit
   * allows.
   */
  private requeue(): void {
    for (const [id, state] of this.states) {
      if (state !== SheetState.Failed) {
        continue;
      }
      if (this.needed(id)) {
        this.states.set(id, SheetState.Queued);
        this.queue.push(id);
      } else {
        this.states.delete(id);
      }
    }
    this.pump();
  }

  /**
   * Starts fetches while the limit allows and sheets wait.
   */
  private pump(): void {
    while (this.fetching < this.limits.fetches && this.queue.length > 0) {
      const id = this.queue.shift() as string;
      this.states.set(id, SheetState.Fetching);
      this.fetching += 1;
      const url = `${this.base}/${corsFetchPath(this.paths[id] as string)}`;
      this.load(
        url,
        (image) => this.finish(id, image, url),
        () => this.finish(id, null, url),
      );
    }
  }

  /**
   * Ends one fetch: makes the texture and tells the listener, or marks the sheet failed and warns once for it.
   *
   * @param id The sheet's id.
   * @param image The loaded image, or null when it failed.
   * @param url Where it was fetched from, for the warning.
   */
  private finish(id: string, image: HTMLImageElement | null, url: string): void {
    if (this.stopped) {
      return;
    }
    this.fetching -= 1;
    const key = sheetTextureKey(id);
    const borrowed = image !== null && this.textures.exists(key);
    if (image !== null && (borrowed || this.textures.addImage(key, image) !== null)) {
      this.states.set(id, borrowed ? SheetState.Borrowed : SheetState.Loaded);
      this.held += 1;
      if (this.failed === 0 && this.fetching === 0 && this.queue.length === 0) {
        this.wait = this.limits.retryMs;
      }
      this.onLoaded(id);
    } else {
      this.states.set(id, SheetState.Failed);
      this.failed += 1;
      if (!this.warned.has(id)) {
        this.warned.add(id);
        console.warn(`look sheet ${id} did not load from ${url}; the looks wearing it are drawn in the fallback look until it does`);
      }
      this.onFailed?.(id);
    }
    this.pump();
  }

  /**
   * Removes every texture the sheets made and stops: fetches still in flight end with nothing made.
   */
  public destroy(): void {
    this.stopped = true;
    for (const [id, state] of this.states) {
      if (state === SheetState.Loaded) {
        this.textures.remove(sheetTextureKey(id));
      }
    }
    this.states.clear();
    this.queue.length = 0;
    this.held = 0;
  }
}
